import { inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { SubscriptionMessage, WebSocketResponse } from '@models/websocket-types';
import { WebSocketService } from './websocket.service';

@Injectable({
  providedIn: 'root'
})
export class WebSocketSubscriptionsService implements OnDestroy {
  private webSocketService = inject(WebSocketService);

  private activeSubscriptions = new Map<
    string,
    {
      subscription: Subscription;
      topic: SubscriptionMessage['topic'];
      destroy$: Subject<void>;
    }
  >();
  private destroy$ = new Subject<void>();
  private topicRefCount = new Map<string, number>();

  /**
   * Subscribe to multiple topics at once
   * @param topics The topics to subscribe to
   */
  public subscribeToTopics(topics: string[]): void {
    if (topics.length === 0) return;

    if (!this.webSocketService.isConnected()) {
      console.warn('WebSocket not connected, cannot subscribe to topics');
      return;
    }

    // Check which topics need subscribing (count === 0)
    const topicsToSubscribe = topics.filter(topic => (this.topicRefCount.get(topic) || 0) === 0);

    if (topicsToSubscribe.length > 0) {
      const message = { action: 'subscribe', topics: topicsToSubscribe } as Record<string, unknown>;
      this.webSocketService.sendMessage(message);
    }

    // Increment counts for all requested topics
    topics.forEach(topic => {
      const current = this.topicRefCount.get(topic) || 0;
      this.topicRefCount.set(topic, current + 1);
    });
  }

  /**
   * Subscribe to specific WebSocket message topics and filter by conditions
   * @param topic The topic to subscribe to
   * @param filterFn The filter function to apply to incoming messages
   * @returns Observable of filtered WebSocket responses
   */
  subscribeToTopicWithFilter(topic: SubscriptionMessage['topic'], filterFn: (message: WebSocketResponse) => boolean): Observable<WebSocketResponse> {
    return new Observable<WebSocketResponse>(subscriber => {
      // Increment ref count and subscribe if first
      this.subscribeToTopics([topic]);

      const messagesSub = this.webSocketService
        .getMessages()
        .pipe(filter(filterFn))
        .subscribe({
          next: message => subscriber.next(message),
          error: error => subscriber.error(error),
          complete: () => subscriber.complete()
        });

      subscriber.add(() => messagesSub.unsubscribe());
    });
  }

  /**
   * Subscribe to a single integration topic and handle its messages with proper filtering
   * @param topic The integration topic to subscribe to
   * @param messageType The message type to filter for
   * @param messageSource The message source to filter for
   * @returns Observable of filtered WebSocket responses
   */
  public subscribeToIntegrationTopic(topic: SubscriptionMessage['topic'], messageType: string, messageSource: string): Observable<WebSocketResponse> {
    return this.subscribeToTopicWithFilter(topic, (message: WebSocketResponse) => {
      // Check if message type matches expected type
      const typeMatches = message.type === messageType;

      // Check if message source matches expected source
      const sourceMatches = message.source === messageSource;

      return typeMatches || sourceMatches;
    });
  }

  /**
   * Subscribe to an integration topic and emit messages that indicate completion (success/error)
   * @param topic The integration topic to subscribe to
   * @param messageType The message type to filter for
   * @param messageSource The message source to filter for
   * @param destroy$ The subject that emits when the subscription should be destroyed
   * @returns Observable of completion messages (success/error only)
   */
  public getIntegrationMessages(
    topic: SubscriptionMessage['topic'],
    messageType: string,
    messageSource: string,
    destroy$: Subject<void>
  ): Observable<WebSocketResponse> {
    return this.subscribeToIntegrationTopic(topic, messageType, messageSource).pipe(
      filter(message => message.status === 'success' || message.status === 'error'),
      take(1),
      takeUntil(destroy$)
    );
  } /**
   * Unsubscribe from a specific topic
   * @param topic The topic to unsubscribe from
   */
  public unsubscribeFromTopic(topic: SubscriptionMessage['topic']): void {
    const current = this.topicRefCount.get(topic) || 0;
    if (current > 0) {
      this.topicRefCount.set(topic, current - 1);
      if (current - 1 === 0) {
        if (this.webSocketService.isConnected()) {
          const message: SubscriptionMessage = {
            action: 'unsubscribe',
            topic
          };
          this.webSocketService.sendMessage(message);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up all active subscriptions
    this.activeSubscriptions.forEach(subDetail => {
      subDetail.destroy$.next();
      subDetail.destroy$.complete();

      if (!subDetail.subscription.closed) {
        subDetail.subscription.unsubscribe();
      }

      this.unsubscribeFromTopic(subDetail.topic);
    });

    this.activeSubscriptions.clear();

    // Clean up topic subscriptions
    this.topicRefCount.forEach((count, topic) => {
      for (let i = 0; i < count; i++) {
        this.unsubscribeFromTopic(topic as SubscriptionMessage['topic']);
      }
    });
    this.topicRefCount.clear();
  }
}
